import {
  ImportantDateEventEnum,
  KindergartenRoleEnum,
  RequestStatusEnum,
} from "@king-ict/eupisi-ui/models";

import { useTypedSelector } from "../../app";
import { PermissionEnum, PhysicalExaminationStatusEnum } from "../../models";
import { usePermission } from "../permission";

export const useBusinessRule = () => {
  const pedagogicalYear = useTypedSelector(
    (s) => s.application.pedagogicalYear
  );
  const user = useTypedSelector((s) => s.application.user);

  const importantDate = useTypedSelector((s) => s.application.importantDate);

  const institutionDetails = useTypedSelector((s) => s.application.institutionDetails);

  const { hasPermission } = usePermission();

  const isAdministrationEnabled = (pedagogicalYearId: number) => {
    return (
      pedagogicalYear.id === pedagogicalYearId &&
      importantDate.eventId === ImportantDateEventEnum.InstitutionAdministration
    );
  };

  const isRequestCreationEnabled = (
    pedagogicalYearId: number,
    enrollmentTypeId: number
  ) => {
    return (
      pedagogicalYear.id === pedagogicalYearId &&
      importantDate.eventId === ImportantDateEventEnum.RequestCreation &&
      importantDate.enrollmentTypeId === enrollmentTypeId
    );
  };

  const canCreateCriterion = (pedagogicalYearId: number) => {
    return (
      hasPermission(PermissionEnum.CriterionCreate) &&
      isAdministrationEnabled(pedagogicalYearId) &&
      ((user.roleId === KindergartenRoleEnum.KindergartenDirector && user.institutionId && !user.enrollmentGroupId)
        || (user.roleId === KindergartenRoleEnum.KindergartenAdmin && !institutionDetails.isEnrollmentGroupForCurrentPedagogicalYear)
        || user.roleId === KindergartenRoleEnum.GroupEnrollmentCriterionAdmin))
  };

  const canUpdateCriterion = (pedagogicalYearId: number) => {
    return (
      hasPermission(PermissionEnum.CriterionUpdate) &&
      isAdministrationEnabled(pedagogicalYearId) &&
      ((user.roleId === KindergartenRoleEnum.KindergartenDirector && user.institutionId && !user.enrollmentGroupId)
        || (user.roleId === KindergartenRoleEnum.KindergartenAdmin && !institutionDetails.isEnrollmentGroupForCurrentPedagogicalYear)
        || user.roleId === KindergartenRoleEnum.GroupEnrollmentCriterionAdmin))
  };

  const canDeleteCriterion = (pedagogicalYearId: number) => {
    return (
      hasPermission(PermissionEnum.CriterionDelete) &&
      isAdministrationEnabled(pedagogicalYearId) &&
      ((user.roleId === KindergartenRoleEnum.KindergartenDirector && user.institutionId && !user.enrollmentGroupId)
        || (user.roleId === KindergartenRoleEnum.KindergartenAdmin && !institutionDetails.isEnrollmentGroupForCurrentPedagogicalYear)
        || user.roleId === KindergartenRoleEnum.GroupEnrollmentCriterionAdmin))
  };

  const canCreateLocationProgramData = (
    pedagogicalYearId: number,
    enrollmentTypeId: number,
    monthId?: number
  ) => {
    return (
      hasPermission(PermissionEnum.LocationProgramDataCreate) &&
      isAdministrationEnabled(pedagogicalYearId) &&
      monthId == importantDate.monthId &&
      importantDate.enrollmentTypeId === enrollmentTypeId
    );
  };

  const canDownloadExcelRequests = (
    pedagogicalYearId: number,
    monthId?: number
  ) => {
    return (
      !importantDate ||
      (pedagogicalYear.id === pedagogicalYearId &&
        importantDate.monthId == monthId &&
        (importantDate.eventId === ImportantDateEventEnum.RequestEvaluation ||
          importantDate.eventId ===
          ImportantDateEventEnum.ComplaintSubmission ||
          importantDate.eventId === ImportantDateEventEnum.ComplaintHandling ||
          importantDate.eventId === ImportantDateEventEnum.ResultAnnouncement ||
          importantDate.eventId === ImportantDateEventEnum.ContractSigning)) ||
      pedagogicalYear.id !== pedagogicalYearId ||
      importantDate.monthId != monthId
    );
  };

  const canUpdateLocationProgramData = (
    pedagogicalYearId: number,
    enrollmentTypeId: number,
    monthId?: number
  ) => {
    return (
      hasPermission(PermissionEnum.LocationProgramDataUpdate) &&
      isAdministrationEnabled(pedagogicalYearId) &&
      monthId == importantDate.monthId &&
      importantDate.enrollmentTypeId === enrollmentTypeId
    );
  };

  const canUpdateLocationProgramDetails = (
    pedagogicalYearId: number,
    enrollmentTypeId: number
  ) => {
    return (
      hasPermission(PermissionEnum.LocationProgramDataUpdate) &&
      pedagogicalYear.id === pedagogicalYearId &&
      importantDate.enrollmentTypeId === enrollmentTypeId
    );
  };

  const canDeleteLocationProgramData = (
    pedagogicalYearId: number,
    enrollmentTypeId: number
  ) => {
    return (
      hasPermission(PermissionEnum.LocationProgramDataDelete) &&
      isAdministrationEnabled(pedagogicalYearId) &&
      importantDate.enrollmentTypeId === enrollmentTypeId
    );
  };

  const canCreateRequest = (
    pedagogicalYearId: number,
    enrollmentTypeId: number
  ) => {
    return (
      hasPermission(PermissionEnum.RequestCreate) &&
      isRequestCreationEnabled(pedagogicalYearId, enrollmentTypeId)
    );
  };

  const canEvaluateRequest = (
    pedagogicalYearId: number,
    requestStatusId: number,
    enrollmentTypeID: number,
    monthId?: number
  ) => {
    return (
      importantDate.enrollmentTypeId === enrollmentTypeID &&
      importantDate.monthId == monthId &&
      importantDate.eventId === ImportantDateEventEnum.RequestEvaluation &&
      pedagogicalYearId === pedagogicalYear.id &&
      requestStatusId === RequestStatusEnum.Submitted &&
      hasPermission(PermissionEnum.RequestEvaluate)
    );
  };

  const canCancelRequestEvaluation = (
    pedagogicalYearId: number,
    requestStatusId: number,
    enrollmentTypeID: number,
    monthId?: number
  ) => {
    return (
      importantDate.enrollmentTypeId === enrollmentTypeID &&
      importantDate.monthId === monthId &&
      importantDate.eventId === ImportantDateEventEnum.RequestEvaluation &&
      pedagogicalYearId === pedagogicalYear.id &&
      requestStatusId === RequestStatusEnum.Evaluated &&
      hasPermission(PermissionEnum.RequestCancelEvaluation)
    );
  };

  const canUnlockRequest = (
    pedagogicalYearId: number,
    requestStatusId: number,
    enrollmentTypeID: number,
    monthId?: number
  ) => {
    return (
      importantDate.enrollmentTypeId === enrollmentTypeID &&
      importantDate.monthId == monthId &&
      importantDate.eventId === ImportantDateEventEnum.RequestEvaluation &&
      pedagogicalYearId === pedagogicalYear.id &&
      requestStatusId === RequestStatusEnum.Submitted &&
      hasPermission(PermissionEnum.RequestUnlock)
    );
  };

  const canEditRequest = (
    pedagogicalYearId: number,
    requestStatusId: number,
    enrollmentTypeID: number,
    createdByAppUserID?: number,
    monthId?: number
  ) => {
    return (
      importantDate.enrollmentTypeId === enrollmentTypeID &&
      importantDate.monthId == monthId &&
      createdByAppUserID === user.userId &&
      pedagogicalYearId === pedagogicalYear.id &&
      hasPermission(PermissionEnum.RequestEdit) &&
      ((importantDate.eventId === ImportantDateEventEnum.RequestCreation &&
        (requestStatusId === RequestStatusEnum.Created ||
          requestStatusId === RequestStatusEnum.Draft)) ||
        (importantDate.eventId === ImportantDateEventEnum.RequestEvaluation &&
          requestStatusId === RequestStatusEnum.Unlocked))
    );
  };

  const canDeleteRequest = (
    pedagogicalYearId: number,
    requestStatusId: number,
    enrollmentTypeID: number,
    createdByAppUserID?: number,
    monthId?: number
  ) => {
    return (
      importantDate.enrollmentTypeId === enrollmentTypeID &&
      importantDate.monthId == monthId &&
      createdByAppUserID === user.userId &&
      importantDate.eventId === ImportantDateEventEnum.RequestCreation &&
      pedagogicalYearId === pedagogicalYear.id &&
      hasPermission(PermissionEnum.RequestDelete) &&
      (requestStatusId === RequestStatusEnum.Created ||
        requestStatusId === RequestStatusEnum.Draft)
    );
  };

  const canCreateUpdateCommentRequest = (
    enrollmentTypeID: number,
    monthId?: number
  ) => {
    return (
      importantDate.enrollmentTypeId === enrollmentTypeID &&
      importantDate.monthId == monthId
    );
  };

  const canUpdatePhysicalExamination = (
    pedagogicalYearId: number,
    requestPhysicalExaminationDataStatusId: number,
    enrollmentTypeID: number,
    monthId?: number
  ) => {
    return (
      importantDate.enrollmentTypeId === enrollmentTypeID &&
      importantDate.monthId == monthId &&
      pedagogicalYearId === pedagogicalYear.id &&
      hasPermission(PermissionEnum.PhysicalExaminationUpdate) &&
      requestPhysicalExaminationDataStatusId ===
      PhysicalExaminationStatusEnum.ReadyForEvaluation
    );
  };

  const canCreateBenefit = (pedagogicalYearId: number) => {
    return (
      hasPermission(PermissionEnum.BenefitCreate) &&
      isAdministrationEnabled(pedagogicalYearId) &&
      (((user.roleId === KindergartenRoleEnum.KindergartenAdmin ||
        user.roleId === KindergartenRoleEnum.KindergartenDirector) &&
        user.institutionId &&
        !user.enrollmentGroupId) ||
        user.roleId === KindergartenRoleEnum.GroupEnrollmentCriterionAdmin)
    );
  };

  const canDeleteBenefit = (pedagogicalYearId: number) => {
    return (
      hasPermission(PermissionEnum.BenefitDelete) &&
      isAdministrationEnabled(pedagogicalYearId) &&
      (((user.roleId === KindergartenRoleEnum.KindergartenAdmin ||
        user.roleId === KindergartenRoleEnum.KindergartenDirector) &&
        user.institutionId &&
        !user.enrollmentGroupId) ||
        user.roleId === KindergartenRoleEnum.GroupEnrollmentCriterionAdmin)
    );
  };

  const canReadBeneficiaryList = () => {
    return user.roleId === KindergartenRoleEnum.Helpdesk
  };

  const canReadImportantDates = () => {
    return user.roleId === KindergartenRoleEnum.Helpdesk
      || user.roleId === KindergartenRoleEnum.CentralSystemAdmin
  };

  const canUpdateImportantDates = () => {
    return user.roleId === KindergartenRoleEnum.CentralSystemAdmin
  };

  const canPhysicalExaminationDocumentUpload = () => {
    return user.roleId === KindergartenRoleEnum.KindergartenAdmin || user.roleId === KindergartenRoleEnum.KindergartenDirector
  };

  const isPhysicalExaminationAllowed = (createdByAppUserID: number | undefined) => {
    return createdByAppUserID != undefined && createdByAppUserID != null && user.userId === createdByAppUserID;
  };

  const canEvaluateEnrollment = () => {
    return hasPermission(PermissionEnum.EnrollmentListUpdate)
      && (user.roleId === KindergartenRoleEnum.KindergartenEnrollmentCommittee
        || user.roleId === KindergartenRoleEnum.KindergartenDirector
        || KindergartenRoleEnum.GroupEnrollmentCommittee)
  };

  const canInitialConversationEnabledUpdate = () => {
    let isGroup = institutionDetails.isEnrollmentGroupForCurrentPedagogicalYear;
    if (isGroup) {
      let notGroupRole = user.roleId === KindergartenRoleEnum.KindergartenAdmin || user.roleId === KindergartenRoleEnum.KindergartenDirector;
      if (notGroupRole) {
        return false;
      }
    }

    return hasPermission(PermissionEnum.InitialConversationEnabledUpdate)
      && importantDate.eventId === ImportantDateEventEnum.InstitutionAdministration;
  };

  return {
    canCreateCriterion,
    canUpdateCriterion,
    canDeleteCriterion,
    canCreateLocationProgramData,
    canUpdateLocationProgramData,
    canUpdateLocationProgramDetails,
    canDeleteLocationProgramData,
    canCreateRequest,
    canEvaluateRequest,
    canUnlockRequest,
    canEditRequest,
    canDeleteRequest,
    canDownloadExcelRequests,
    canCreateUpdateCommentRequest,
    canUpdatePhysicalExamination,
    canCreateBenefit,
    canDeleteBenefit,
    canReadBeneficiaryList,
    canReadImportantDates,
    canUpdateImportantDates,
    canPhysicalExaminationDocumentUpload,
    isPhysicalExaminationAllowed,
    canCancelRequestEvaluation,
    canEvaluateEnrollment,
    canInitialConversationEnabledUpdate
  };
};
